import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TrendingUp, MapPin, Target } from "lucide-react";

export default function ProgressPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/progress").then(({ data }) => setData(data));
  }, []);

  if (!data) return <div className="p-10 text-gray-500">Loading…</div>;

  const activeVillages = data.villages.filter((v) => v.done > 0).length;
  const ranked = [...data.villages].sort((a, b) => b.done - a.done);
  const maxDone = Math.max(1, ...ranked.map((v) => v.done));

  // Group by mandal for compact lists
  const byMandal = ranked.reduce((acc, v) => {
    const m = v.mandal || "Other";
    if (!acc[m]) acc[m] = [];
    acc[m].push(v);
    return acc;
  }, {});

  const MANDAL_COLOR = {
    Pithapuram: "#1E3A8A",
    Gollaprolu: "#F59E0B",
    Kothapalle: "#E11D48",
  };

  return (
    <div className="p-6 lg:p-8" data-testid="progress-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading font-bold text-3xl">Survey Progress</h1>
          <p className="text-gray-600 text-sm">Live registrations across {data.villages.length} villages in 3 mandals of Pithapuram constituency.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        <Kpi
          color="#1E3A8A"
          icon={<Target />}
          label="Total Target"
          value={data.total_target.toLocaleString()}
          sub={`Across ${data.villages.length} villages · 3 mandals`}
          data-testid="kpi-total-target"
        />
        <Kpi
          color="#10B981"
          icon={<TrendingUp />}
          label="Total Registered"
          value={data.total_done.toLocaleString()}
          sub={`${data.overall_pct}% of target`}
          data-testid="kpi-registered"
        />
        <Kpi
          color="#F59E0B"
          icon={<MapPin />}
          label="Villages with registrations"
          value={activeVillages.toLocaleString()}
          sub={`of ${data.villages.length} villages`}
          data-testid="kpi-active-villages"
        />
      </div>

      {/* Constituency-wide progress bar */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-5 mb-6" data-testid="overall-progress-bar">
        <div className="flex items-center justify-between mb-2">
          <div className="font-heading font-semibold text-sm text-gray-700">Pithapuram Constituency progress</div>
          <div className="text-sm font-heading font-bold tabular-nums text-[#1E3A8A]">
            {data.total_done.toLocaleString()} / {data.total_target.toLocaleString()} ({data.overall_pct}%)
          </div>
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${Math.min(100, data.overall_pct || (data.total_done > 0 ? 1 : 0))}%`,
              background: "linear-gradient(90deg,#10B981 0%,#0EA5E9 60%,#1E3A8A 100%)",
            }}
          />
        </div>
      </div>

      {/* Compact village-wise list grouped by mandal */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_2px_10px_rgba(0,0,0,0.04)] p-6" data-testid="village-update-section">
        <h3 className="font-heading font-bold text-lg mb-4">Village-wise update</h3>
        <div className="grid lg:grid-cols-3 gap-x-8 gap-y-6">
          {Object.entries(byMandal).map(([mandal, villages]) => {
            const color = MANDAL_COLOR[mandal] || "#64748B";
            const mandalDone = villages.reduce((s, v) => s + v.done, 0);
            return (
              <div key={mandal} data-testid={`village-update-mandal-${mandal}`}>
                <div className="flex items-center justify-between pb-2 mb-2 border-b" style={{ borderColor: `${color}30` }}>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                    <span className="font-heading font-bold text-sm" style={{ color }}>{mandal}</span>
                    <span className="text-xs text-gray-400">· {villages.length} villages</span>
                  </div>
                  <span className="text-sm font-heading font-bold tabular-nums" style={{ color }}>{mandalDone.toLocaleString()}</span>
                </div>
                <ul className="space-y-1.5">
                  {villages.map((v) => {
                    const w = (v.done / maxDone) * 100;
                    const dim = v.done === 0 ? "text-gray-400" : "text-gray-800";
                    return (
                      <li key={v.village_id} className="text-sm" data-testid={`village-update-row-${v.name}`}>
                        <div className="flex justify-between items-center gap-2">
                          <span className={`truncate ${dim}`}>{v.name}</span>
                          <span className={`font-heading font-bold tabular-nums shrink-0 ${dim}`}>{v.done}</span>
                        </div>
                        <div className="h-1 bg-gray-100 rounded-full overflow-hidden mt-0.5">
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${w}%`, backgroundColor: color }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Kpi({ color, icon, label, value, sub, "data-testid": testId }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5" data-testid={testId}>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white mb-3" style={{ backgroundColor: color }}>{icon}</div>
      <div className="font-heading font-bold text-3xl tracking-tighter">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-1">{sub}</div>}
    </div>
  );
}
